import { useMemo, useRef } from 'react';
import { TeamInfo } from '../TeamInfo';
import { RaceRules } from '../types';
import {
  derivePitHistory,
  emptyPitStopStore,
  observePitStops,
  PitStopStore,
} from '../analytics/pitStops';
import { computeStrategy, StrategySummary } from '../analytics/strategy';

export interface UseStrategyOptions {
  rules: RaceRules;
  sessionEndsAt: number | null;
  /** Teams treated as ours — their stops define our pit loss. */
  ourTeamIds?: Set<string>;
  /** Overrides the clock; replay supplies its virtual time. */
  now?: () => number;
}

/**
 * Strategy over the live (or replayed) grid.
 *
 * Recomputes whenever the team map changes, which during a race is many times a
 * second — so no separate timer is needed to keep the countdown-derived numbers
 * moving. Pausing a replay correctly freezes them.
 *
 * Folding pit observations inside `useMemo` mutates a ref during render, which
 * is only safe because the store is keyed by pit count and therefore idempotent:
 * StrictMode's double render and a replay's backwards seek both re-observe the
 * same stops without duplicating them.
 */
export function useStrategy(
  teams: Map<string, TeamInfo>,
  { rules, sessionEndsAt, ourTeamIds, now }: UseStrategyOptions,
): StrategySummary {
  const storeRef = useRef<PitStopStore>(emptyPitStopStore());

  return useMemo(() => {
    const store = storeRef.current;
    observePitStops(store, teams);

    return computeStrategy({
      teams,
      pitHistory: derivePitHistory(store, teams),
      rules,
      sessionEndsAt,
      now: now ? now() : Date.now(),
      ourTeamIds,
    });
  }, [teams, rules, sessionEndsAt, ourTeamIds, now]);
}
